import React, { useEffect, useMemo } from 'react'
import { toUSNumberFormat } from '@/v2/utils/format'
import Typography from '@/v2/components/common/Typography'
import Divider from '@/v2/components/common/Divider'
import ActionButton from '@/v2/components/common/ActionButton'
import {
  usePatientFinancePaymentMethods,
  usePaymentPlan,
} from '@/v2/hooks/usePatientFinance'
import { useForm } from 'react-hook-form'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Wrapper from '@/v2/components/common/Wrapper'
import { revertCreatePaymentFormFormat } from '@/v2/utils/helpers'
import { initCreatePaymentValues } from '../CreatePaymentStateContext/CreatePaymentStateContext'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'

const PlanConfirm = ({ patient, state, setState, onClose }) => {
  const { createPaymentPlan } = usePaymentPlan({
    patientId: patient.id,
  })
  const { onOpenSnackbar } = useSnackbarContext()

  const toCurrency = val => toUSNumberFormat(val)

  const { creditCards } = usePatientFinancePaymentMethods({
    patientId: patient.id,
  })

  const card = useMemo(() => {
    const findCardById = creditCards?.find(p => p.id === state.paymentMethodId)
    return findCardById
  }, [creditCards, state.paymentMethodId])

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useForm({
    defaultValues: state,
  })

  const onSubmit = async data => {
    try {
      const installments =
        data.classification === 'one_time_payment' ? 1 : data.installments
      const downPayment = data.downPayment || data.contract?.downPayment || 0

      const monthlyPaymentCount = installments

      const noPaymentMethod = data.paymentMethodType === 'none'
      const isPaymentLink = data.paymentMethodType === 'payment_link'

      const payload = {
        description: data.description,
        amount: Number(data.amount),
        installments: monthlyPaymentCount,
        discountRate: Number(data.discountRate),
        interestRate: Number(data.interestRate),
      }

      if (
        data.processingFeePercentage !== '' &&
        data.processingFeePercentage != null
      ) {
        payload.processingFeePercentage = Number(data.processingFeePercentage)
      }

      if (downPayment > 0) {
        payload.downPayment = downPayment
      }

      if (!noPaymentMethod && data.paymentMethodId) {
        payload.paymentMethodId = data.paymentMethodId
      }

      if (isPaymentLink) {
        payload.sendPaymentLink = true
      }

      if (data.contractId) {
        payload.contract_id = data.contractId
      }

      const res = await createPaymentPlan(payload)

      if (res.paymentPlan?.lastErrorMessage) {
        throw new Error(res.paymentPlan.lastErrorMessage)
      }

      if (res.paymentPlan?.errors) {
        const errors =
          typeof res.paymentPlan.errors === 'string'
            ? JSON.parse(res.paymentPlan.errors)
            : res.paymentPlan.errors

        if (errors?.contract_id) {
          throw new Error(`Contract ${errors?.contract_id[0]}`)
        }
      }
      if (res.status === 'denied') {
        throw new Error(
          'Payment plan creation failed. Card declined. Please try again. If the problem persists, please contact support.'
        )
      }

      onOpenSnackbar('Payment plan created successfully!', 'success')
    } catch (error) {
      onOpenSnackbar(error.message, 'error')
      const revertForm = revertCreatePaymentFormFormat(data)
      setState({ ...revertForm, step: 1, error: error.message })
    }
  }

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset()
      setState(initCreatePaymentValues)
      onClose()
    }
  }, [isSubmitSuccessful, onClose, reset, setState])

  const handleBack = () => {
    const revertForm = revertCreatePaymentFormFormat(state)
    setState({ ...revertForm, step: 1 })
  }

  const initialPayment = state.downPayment || state?.contract?.downPayment || 0
  const installments = state.updatedInstallments || state.installments || 1
  const isInstallmentPlan = state.classification === 'installments'
  const hasDiscount = state.discountRate > 0
  const hasInterest = state.interestRate > 0
  const monthlyPaymentCount =
    initialPayment > 0 ? installments - 1 : installments

  const rawProcessingFee = state.processingFeePercentage
  const enteredProcessingFee =
    rawProcessingFee !== '' && rawProcessingFee != null
      ? Number(rawProcessingFee)
      : 0
  // Payment Link counts as a card rail because the patient may pay the
  // invoice by credit card; ACH and "choose later" never incur the fee.
  const cardRailMethod =
    state.paymentMethodType === 'card' ||
    state.paymentMethodType === 'payment_link'
  const displayedProcessingFee = cardRailMethod ? enteredProcessingFee : 0

  return (
    <FormControl
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      variant="filled"
    >
      <Box
        bgcolor={'gray.light'}
        display={'flex'}
        flexDirection={'column'}
        gap={2}
        borderRadius={2}
        px={2}
        py={2}
      >
        {/* Plan Details Section */}
        <Box>
          <Typography variant="body" bold sx={{ mb: 1.5 }}>
            Plan Details
          </Typography>
          <Wrapper>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body">{state.description}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Original Amount
              </Typography>
              <Typography variant="body">
                ${toCurrency(state.amount)}
              </Typography>
            </Box>

            {hasDiscount && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Discount Applied
                </Typography>
                <Typography variant="body" bold color="success.dark">
                  -{state.discountRate}%
                </Typography>
              </Box>
            )}

            {hasDiscount && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Amount After Discount
                </Typography>
                <Typography variant="body" bold>
                  ${toCurrency(state.amountWithDiscount)}
                </Typography>
              </Box>
            )}

            {hasInterest && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Interest Rate
                </Typography>
                <Typography variant="body" bold>
                  +{state.interestRate}%
                </Typography>
              </Box>
            )}
          </Wrapper>
        </Box>

        <Divider margin="0 4px" />

        {/* Payment Schedule Section */}
        <Box>
          <Typography variant="body" bold sx={{ mb: 1.5 }}>
            Payment Schedule
          </Typography>
          <Wrapper>
            {initialPayment > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Initial Payment (Due Today)
                </Typography>
                <Typography variant="body" bold>
                  ${toCurrency(initialPayment)}
                </Typography>
              </Box>
            )}

            {isInstallmentPlan && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Monthly Payments
                </Typography>
                <Typography variant="body">
                  {monthlyPaymentCount} payment
                  {monthlyPaymentCount !== 1 ? 's' : ''} of $
                  {toCurrency(state.installmentAmount)}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Amount{hasInterest ? ' (with interest)' : ''}
              </Typography>
              <Typography variant="body" bold>
                ${toCurrency(state.totalDue)}
              </Typography>
            </Box>
          </Wrapper>
        </Box>

        <Divider margin="0 4px" />

        {/* Payment Method Section */}
        <Box>
          <Typography variant="body" bold sx={{ mb: 1.5 }}>
            Payment Information
          </Typography>
          <Wrapper>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Payment Method
              </Typography>
              {state.paymentMethodType === 'card' ? (
                <Typography variant="body">
                  Card ending in {card.last4}
                </Typography>
              ) : state.paymentMethodType === 'none' ? (
                <Typography variant="body" bold color="warning.main">
                  Choose Later
                </Typography>
              ) : state.paymentMethodType === 'ach' ? (
                <Typography variant="body">Bank Account</Typography>
              ) : (
                <Typography variant="body">Payment Link (via email)</Typography>
              )}
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Processing Fee
                {state.paymentMethodType === 'payment_link'
                  ? ' (if paid by credit card)'
                  : ''}
              </Typography>
              <Typography variant="body">
                {displayedProcessingFee}%
              </Typography>
            </Box>

            {state.contract && state.contract.patientLegalName !== '' && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Contract Signer
                </Typography>
                <Typography variant="body">
                  {state.contract?.patientLegalName}
                </Typography>
              </Box>
            )}
          </Wrapper>
        </Box>
      </Box>

      <Box bgcolor={'gray.light'} borderRadius={2} px={2} py={2}>
        {state.paymentMethodType === 'card' ? (
          <Box display={'flex'} flexDirection={'column'} gap={1}>
            <Typography variant="body">Receipt will be emailed to:</Typography>
            <Typography variant="body" bold>
              {patient.email}
            </Typography>
          </Box>
        ) : state.paymentMethodType === 'none' ? (
          <Typography variant="body">
            The payment method has not been defined.
            <br />
            <br />
            {state.classification !== 'installments'
              ? 'Keep in mind that for a single installment plan, the payment will need to be manually charged. Make sure to add a payment method later and charge it accordingly'
              : 'Please note that the first payment is required to be manually charged, so ensure you add a payment method and set up the remaining invoices to use this added method.'}
          </Typography>
        ) : state.paymentMethodType === 'payment_link' ? (
          <Typography variant="body">
            The first invoice will be emailed to: <span> {patient.email}</span>.
            <br />
            <br />
            {enteredProcessingFee > 0 && (
              <>
                If the patient pays this invoice with a credit card, a
                processing fee of {enteredProcessingFee}% will be applied.
                <br />
                <br />
              </>
            )}
            The patient pays this invoice, and we save the payment method on the
            gateway for the subsequent invoices if had.
          </Typography>
        ) : (
          <Typography variant="body">
            The first invoice will be emailed to: <span> {patient.email}</span>.
            <br />
            <br />
            The patient pays this invoice, and we save the payment method on the
            gateway for the subsequent invoices if had.
          </Typography>
        )}
      </Box>

      <Grid container gap={2} justifyContent="flex-end">
        <ActionButton
          variant="text"
          color="primary"
          disabled={isSubmitting}
          onClick={handleBack}
        >
          Back
        </ActionButton>
        <ActionButton
          variant="contained"
          color="secondary"
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Confirm
        </ActionButton>
      </Grid>
    </FormControl>
  )
}

export default PlanConfirm
